import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FilterParams } from '@claranet/node-search-filter';
import { ErrorService } from '@app/services/error.service';
import { toParams } from '@shared/utils/query-to-params';
import { EmbedTribe, Tribe, embed as tribeEmbedded, configuration as tribeConfiguration, AnalyticsCodes } from '../models/tribe.model';
import { uniq } from 'lodash';
import { Embedded } from '@data/models/embed.model';
import { MetadataService } from './metadata.service';
import { ListMetadataKeys, MetadataLine } from '@data/models/metadata.model';
import { PartialRequired } from '@shared/utils/utilities';

export const TRIBE_SERVICE_API_ROOT = '/core/v1/tribes';
export const ORGANIZATIONS_SERVICE_API_ROOT = '/core/v1/organizations';

@Injectable()
export class TribeService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
        private metadataService: MetadataService,
    ) {
        this.errors = this.errorService.tribeServiceErrors;
    }

    /**
     * Get sorted tribes list
     *
     * @returns {Observable<Tribe[]>} - The tribe list
     *
     * @memberof TribeService
     */
    public findTribes(filterParams?: FilterParams): Observable<Tribe[]> {
        const params = toParams(filterParams);
        return this.http.get<{ tribes: Tribe[] }>(TRIBE_SERVICE_API_ROOT, { params }).pipe(
            map((result) => result.tribes.map(tribe => new Tribe(tribe))),
            catchError(this.errorService.errorHandler<Tribe[]>(this.errors, 'Récupération des tribus', []))
        );
    }

    public getTribe(tribeId: string): Observable<Tribe> {
        return this.http.get<Tribe>(TRIBE_SERVICE_API_ROOT + '/' + tribeId).pipe(
            map((tribe) => new Tribe(tribe)),
            catchError(this.errorService.errorHandler<Tribe>(this.errors, 'Récupération des données de tribu', null))
        );
    }

    public getEmbeddedTribe<T extends tribeEmbedded>(tribeId: string, embed: T[] = []): Observable<EmbedTribe<T>> {
        const embeds = uniq(embed.map((it) => tribeConfiguration[it].embed)).join(',');
        return this.http.get<Tribe & { embedded: Embedded }>(TRIBE_SERVICE_API_ROOT + '/' + tribeId, { params: { embed: embeds } }).pipe(
            map((tribe) => Tribe.toCustomTribe(tribe, tribe.embedded, embed)),
            catchError(this.errorService.errorHandler<EmbedTribe<T>>(this.errors, 'Récupération des données de tribu', null))
        );
    }

    public updateTribe(tribe: PartialRequired<Tribe, 'id'>): Observable<Tribe> {
        return this.http.patch<Tribe>(TRIBE_SERVICE_API_ROOT + '/' + tribe.id, tribe).pipe(
            map((updated) => new Tribe(updated)),
            catchError(this.errorService.errorHandler<Tribe>(this.errors, 'Enregistrement de la tribu', null))
        );
    }

    public export(): Observable<{ content: string; mimeType: string }> {
        return this.http.get<{ content: string; mimeType: string }>(`${ORGANIZATIONS_SERVICE_API_ROOT}:export`).pipe(
            catchError(this.errorService.errorHandler(this.errors, 'Export de organisation', null)),
        );
    }

    public updateMetadata(tribeId: string, { key, value }: MetadataLine): Observable<Tribe> {
        return this.http.put<Tribe>(`${TRIBE_SERVICE_API_ROOT}/${tribeId}/metadata/${encodeURIComponent(key)}`, { value }).pipe(
            map((tribe) => new Tribe(tribe)),
            catchError(this.errorService.errorHandler<Tribe>(this.errors, 'Modification des metadatas de la tribu', null))
        );
    }

    public deleteMetadata(tribeId: string, key: string): Observable<Tribe> {
        return this.http.delete<Tribe>(`${TRIBE_SERVICE_API_ROOT}/${tribeId}/metadata/${encodeURIComponent(key)}`).pipe(
            map((tribe) => new Tribe(tribe)),
            catchError(this.errorService.errorHandler<Tribe>(this.errors, 'Suppression d\'une metadata de la tribu', null))
        );
    }

    public fetchMetadataKeys(filterParams: FilterParams): Observable<ListMetadataKeys> {
        return this.metadataService.fetchMetadataKeys(TRIBE_SERVICE_API_ROOT, filterParams, this.errors);
    }

    public fetchAnalyticsCodes(filterParams: FilterParams): Observable<AnalyticsCodes> {
        const params = toParams(filterParams);
        return this.http.get<{ analyticsCodes: AnalyticsCodes }>(`${TRIBE_SERVICE_API_ROOT}/analytics-codes`, { params }).pipe(
            map((result) => result.analyticsCodes),
            catchError(this.errorService.errorHandler<AnalyticsCodes>(this.errors, 'Récupération des analytics codes des tribus', null))
        );
    }

}
